import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useContext, useState } from 'react';
import UserContext from '../../../../../../shared-components/contexts/UserContext';
import UserSearch from '../../../../../../shared-components/UserSearch';
import { ICoreUser } from '../../../../../../shared-interfaces/User/ICoreUser';
import CookieService from '../../../../../../shared-services/CookieService';
import { Role } from '../../../../../../shared-types/Roles';
import ConnectionsService from '../Services/ConnectionsService';

export interface AddNewConnectionProps {
  open: boolean;
  onClose: () => void;
  student: ICoreUser;
  onAddConnections: (advisors: ICoreUser[]) => void;
}

const AddNewConnectionDialog = (props: AddNewConnectionProps) => {
  const user = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addedAdvisors, setAddedAdvisors] = useState<ICoreUser[]>([]);

  const handleSubmit = () => {
    setIsSubmitting(true);
    ConnectionsService.addAdvisorConnections(
      user.id,
      props.student.id,
      addedAdvisors.map(u => {
        return u.id;
      }),
    )
      .then(() => {
        props.onAddConnections(addedAdvisors);
      })
      .then(() => {
        enqueueSnackbar('Connections Added', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Something went wrong', { variant: 'error' });
      })
      .finally(() => {
        setIsSubmitting(false);
        setAddedAdvisors([]);
        CookieService.Create(
          `BeaconConnectionChanged_${props.student.id}`,
          'true',
          undefined,
          undefined,
          'lax',
          5,
        );
        props.onClose();
      });
  };

  return (
    <Dialog
      aria-labelledby="add-new-connection-title"
      open={props.open}
      onClose={props.onClose}
    >
      <DialogTitle id="add-new-connection-title">
        Add New Connection
      </DialogTitle>
      <form>
        <DialogContent>
          <DialogContentText>
            Find individual(s) to add to this student's network by searching their name or ID.
          </DialogContentText>
          <UserSearch
            label="Name or External ID of Individual(s) (Max 10)"
            value={addedAdvisors}
            onSelect={u => setAddedAdvisors(u as ICoreUser[])}
            roleRestriction={[
              Role.PeerAdvisor,
              Role.Advisor,
              Role.Generalist,
              Role.Executive,
              Role.Administrator,
            ]}
            multiple
          />
          {addedAdvisors.length > 10 && (
            <Typography color="error">
              Only 10 advisors can be added at a time. To add more, use the
              Connections Import.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} color="primary" variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={
              addedAdvisors.length === 0 ||
              addedAdvisors.length > 10 ||
              isSubmitting
            }
          >
            Add Advisors
          </Button>
        </DialogActions>
        {isSubmitting && <LinearProgress />}
      </form>
    </Dialog>
  );
};

export default AddNewConnectionDialog;
